/**
 * Template testing utilities to detect and diagnose common issues
 */

import supabase from '../supabaseUtils';

/**
 * Tests loading a template from Supabase and verifies the data structure
 * @param {string} templateId The ID of the template to test
 * @returns {Promise<Object>} Result object with test results
 */
export async function testTemplateLoading(templateId) {
  console.log(`Starting template load test for ID: ${templateId}`);
  const results = {
    templateExists: false,
    hasSceneUrl: false,
    sceneContentLoaded: false,
    sceneDataValid: false,
    fontReferences: [],
    errors: []
  };

  try {
    // Step 1: Check if template exists in database
    const { data: template, error } = await supabase
      .from('templates')
      .select('*')
      .eq('id', templateId)
      .single();
    
    if (error || !template) {
      results.errors.push(`Template not found: ${error?.message || 'Unknown error'}`);
      return results;
    }
    
    results.templateExists = true;
    console.log(`Template found: ${template.name}`);
    
    // Step 2: Verify scene URL
    if (!template.scene_url) {
      results.errors.push('Template has no scene URL');
      return results;
    }
    
    results.hasSceneUrl = true;
    console.log(`Scene URL: ${template.scene_url}`);
    
    // Step 3: Try to load scene data
    const sceneUrl = template.scene_url;
    try {
      const response = await fetch(sceneUrl, { 
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      
      if (!response.ok) {
        results.errors.push(`Failed to fetch scene: ${response.status} ${response.statusText}`);
        return results;
      }
      
      results.sceneContentLoaded = true;
      console.log('Scene content loaded successfully');
      
      // Step 4: Check if scene data is valid
      const sceneText = await response.text();
      if (!sceneText.startsWith('UBQ')) {
        results.errors.push('Invalid scene data format (does not start with UBQ)');
        return results;
      }
      
      results.sceneDataValid = true;
      console.log(`Scene data valid, size: ${sceneText.length} bytes`);
      
      // Step 5: Scan for font references in the scene data
      const fontMatches = sceneText.match(/fonts\/[A-Za-z0-9_\/\-\.]+\.ttf/g) || [];
      results.fontReferences = [...new Set(fontMatches)]; // Deduplicate
      
      console.log(`Found ${results.fontReferences.length} font references in the scene:`);
      results.fontReferences.forEach(font => console.log(` - ${font}`));
      
      // Todo: Add more detailed checks here if needed
      
    } catch (fetchError) {
      results.errors.push(`Error fetching scene: ${fetchError.message}`);
    }
    
  } catch (testError) {
    results.errors.push(`Test failed: ${testError.message}`);
  }
  
  return results;
}

/**
 * Test the availability of font files referenced by templates
 * @param {Array<string>} fontPaths Array of font paths to test
 * @returns {Promise<Object>} Results of font availability tests
 */
export async function testFontAvailability(fontPaths) {
  const results = {
    fontsChecked: 0,
    fontsAvailable: 0,
    details: [],
    errors: []
  };
  
  try {
    if (!fontPaths || !fontPaths.length) {
      results.errors.push('No font paths provided');
      return results;
    }
    
    results.fontsChecked = fontPaths.length;
    console.log(`Testing ${fontPaths.length} fonts for availability...`);
    
    // Test each font path
    for (const fontPath of fontPaths) {
      // Create full paths for both relative and absolute URLs
      const relativePath = `/cesdk-assets/imglyAssets/ly.img.typeface/${fontPath}`;
      
      try {
        // Try the relative path first
        const response = await fetch(relativePath, { method: 'HEAD' });
        
        const status = {
          fontPath,
          url: relativePath,
          status: response.status,
          available: response.ok,
        };
        
        console.log(`Font ${fontPath}: ${response.ok ? 'Available' : 'Not available'} (${response.status})`);
        
        if (response.ok) {
          results.fontsAvailable++;
        }
        
        results.details.push(status);
      } catch (fontError) {
        results.details.push({
          fontPath,
          url: relativePath, 
          error: fontError.message,
          available: false
        });
        
        console.error(`Error testing font ${fontPath}:`, fontError);
      }
    }
    
  } catch (testError) {
    results.errors.push(`Font test failed: ${testError.message}`);
  }
  
  return results;
} 